import type { SxProps, Theme } from '@mui/material/styles';
import { type DataGridProps } from '@mui/x-data-grid';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid/DataGrid';
import type { GridActionsColDef, GridColDef, GridSingleSelectColDef, GridValidRowModel } from '@mui/x-data-grid/models';
import React from 'react';

import { getColumnFilterOperators } from './getColumnOperators';
import { getColumnSize } from './getColumnSize';

const transformColumns = <T extends GridValidRowModel>(columns: DataGridColumn<T>[]): GridColDef<T>[] => {
  return columns.map((column) => {
    return {
      ...column,
      ...getColumnSize(column.columnSize),
      ...getColumnFilterOperators(column.type, column.filterOperators),
    };
  });
};

type ColumnBase<T extends GridValidRowModel> = GridColDef<T> | GridActionsColDef<T> | GridSingleSelectColDef<T>;
export type DataGridColumn<T extends GridValidRowModel> = ColumnBase<T> & {
  columnSize?: 'small' | 'medium' | 'standard' | 'large' | 'xLarge';
};

export type Props<T extends GridValidRowModel> = Omit<DataGridProps, 'columns'> & {
  columns: DataGridColumn<T>[];
};
export const DataGrid = <T extends GridValidRowModel>(props: Props<T>) => {
  const { sx, ...restProps } = props;
  const columns = transformColumns<T>(props.columns);
  const baseSx: SxProps<Theme> = {
    '& .MuiDataGrid-columnHeaderTitle': {
      color: (theme) => theme.palette.neutral[60],
    },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    overflowX: 'scroll',
  };

  return (
    <MuiDataGrid<T>
      showColumnVerticalBorder
      showCellVerticalBorder
      autoHeight
      density="standard"
      sx={[baseSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...restProps}
      columns={columns}
      disableVirtualization={process.env.NODE_ENV === 'test'}
    />
  );
};
